/** THE GOAL GAME SETTINGS OF ONE PRIZE — the timer, when it can be used, the
 *  odds weight, Active and what a star pick does.
 *
 *  ONE component, shown in TWO places: the Goal Game prize row and the gift's
 *  own settings screen in the Gift Generator. Both read and write the SAME
 *  stored prize row through the SAME endpoint, so there is never a second copy
 *  of any of these values. Every control saves on the spot and can be put back
 *  the same way, in one tap.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import api from '../../../lib/api';

const field = "rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";
// A normal-sized button, big enough for a thumb on an iPad.
const clearBtn = "rounded-lg border border-slate-300 dark:border-slate-600 "
    + "px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 "
    + "hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors "
    + "disabled:opacity-50";


// ── the game night's clock ───────────────────────────────────────────
// A prize timer is a CLOCK TIME inside the 03:00→03:00 game night the Goal
// Game already runs on, in its own America/Toronto zone. Minutes are counted
// from 03:00, so a time before 03:00 lands at the END of the night — which is
// exactly what "after midnight of the same night" means. No new setting.
const NIGHT_START_MIN = 3 * 60;
export const nightMinutes = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return ((h * 60 + m) - NIGHT_START_MIN + 1440) % 1440;
};
export const torontoClock = () => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Toronto', hour12: false, hour: '2-digit', minute: '2-digit',
}).format(new Date());
export const prettyTime = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
};

/** "Live now" / "Not live now (starts 9:30 PM)" — updates by itself. */
export const TimerChip = ({ row, testid }) => {
    const { t } = useTranslation();
    const [clock, setClock] = useState(torontoClock());
    useEffect(() => {
        const id = setInterval(() => setClock(torontoClock()), 20000);
        return () => clearInterval(id);
    }, []);
    if (!row.timer_enabled) return null;
    const now = nightMinutes(clock);
    const from = row.timer_from ? nightMinutes(row.timer_from) : 0;
    const to = row.timer_to ? nightMinutes(row.timer_to) : 1439;
    const live = now >= from && now <= to;
    const label = live
        ? t('goalGame.prizeLiveNow')
        : (now < from
            ? t('goalGame.prizeNotLiveStarts', { time: prettyTime(row.timer_from) })
            : t('goalGame.prizeNotLiveEnded', { time: prettyTime(row.timer_to) }));
    return (
        <span data-testid={testid}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  live ? 'bg-emerald-500/15 text-emerald-600' : 'bg-slate-500/15 text-slate-500'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {label}
        </span>
    );
};

/** "In the bar" / "At home" / "In the bar and at home" — who can win a prize.
 *  The same stored field the select writes, read back as a small chip. */
export const whoOf = (row) => {
    const stored = String(row?.who_wins || '').toLowerCase();
    if (stored === 'bar' || stored === 'home' || stored === 'both') return stored;
    return row?.kind === 'gift_card_next_game' ? 'both' : 'bar';
};

export const WhoChip = ({ row, testid }) => {
    const { t } = useTranslation();
    const who = whoOf(row);
    const tone = who === 'home'
        ? 'bg-sky-500/15 text-sky-600'
        : (who === 'both' ? 'bg-violet-500/15 text-violet-600' : 'bg-amber-500/15 text-amber-600');
    return (
        <span data-testid={testid}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
            {t(`goalGame.prizeWho_${who}`)}
        </span>
    );
};

export const PrizeGoalSettings = ({ prize, orgId, promotionId, onChanged, showWeight = true,
                                   expiryDays = 0, onExpiryDays = null }) => {
    const { t } = useTranslation();
    const [row, setRow] = useState(prize);
    const [busy, setBusy] = useState(false);
    // HOW LONG A GIFT CARD STAYS VALID is the venue's own gift-card setting
    // ("Expires after (days)" under the nightly limits) — the SAME stored
    // value, read and written through the SAME endpoint. No second setting.
    const [days, setDays] = useState(Number(expiryDays) || 30);
    const [baseDays, setBaseDays] = useState(0);
    const [daysBusy, setDaysBusy] = useState(false);
    useEffect(() => {
        // The venue's real number arrives a moment after the screen opens; the
        // baseline "Put back" goes home to is that real one, not the default.
        const real = Number(expiryDays) || 0;
        if (!real) return;
        setDays(real);
        setBaseDays((b) => b || real);
    }, [expiryDays]);
    useEffect(() => { setRow(prize); }, [prize]);

    const pid = promotionId !== undefined ? promotionId : (prize.promotion_id || null);

    const patch = async (body) => {
        setRow((r) => ({ ...r, ...body }));
        setBusy(true);
        try {
            const res = await api.patch(`/goal-game/admin/prizes/${prize.id}`, {
                organization_id: orgId, promotion_id: pid || null, ...body,
            });
            setRow(res.data.prize);
            if (onChanged) await onChanged();
        } catch (e) {
            setRow(prize);
            toast.error(e.response?.data?.detail === 'last_active_prize'
                ? 'At least one prize must stay active'
                : 'Could not save that');
        } finally { setBusy(false); }
    };

    const id = prize.id;
    // The forced answers: a gift card for the next game, and any prize set to
    // At home. Nothing to choose, so nothing is shown as a control.
    const useLocked = row.kind === 'gift_card_next_game' || whoOf(row) === 'home';

    const saveDays = async (next) => {
        const value = Math.max(1, Number(next) || 1);
        setDays(value);
        setDaysBusy(true);
        try {
            const read = await api.get('/goal-game/admin/gift-card-settings',
                                       { params: { organization_id: orgId } });
            const cur = read.data.settings || {};
            const res = await api.put('/goal-game/admin/gift-card-settings', {
                organization_id: orgId,
                max_cards_per_night: Number(cur.max_cards_per_night) || 0,
                max_value_per_night: Number(cur.max_value_per_night) || 0,
                max_per_fan_per_game: Number(cur.max_per_fan_per_game) || 0,
                expiry_days: value,
            });
            const saved = Number(res.data.settings?.expiry_days) || value;
            setDays(saved);
            if (onExpiryDays) onExpiryDays(saved);
        } catch (_e) {
            setDays(Number(expiryDays) || 30);
            toast.error('Could not save that');
        } finally { setDaysBusy(false); }
    };

    // iOS Safari's own "Reset" inside the time wheel does not always tell the
    // page the box was emptied. If the box is empty when it loses focus and we
    // still hold a time, that counts as cleared — same as tapping Clear.
    const clearedOnBlur = (e, key) => {
        if (!e.target.value && row[key]) patch({ [key]: '' });
    };
    return (
        <div className="space-y-2" data-testid={`gg-prize-settings-${id}`}>
            <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-500">
                    <input type="checkbox" checked={!!row.timer_enabled} disabled={busy}
                           onChange={(e) => patch({ timer_enabled: e.target.checked })}
                           data-testid={`gg-prize-timer-${id}`} />
                    {t('goalGame.prizeTimer')}
                </label>
                {row.timer_enabled && (
                    <>
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                            {t('goalGame.prizeTimerFrom')}
                            <input type="time" value={row.timer_from || ''}
                                   onChange={(e) => patch({ timer_from: e.target.value })}
                                   onBlur={(e) => clearedOnBlur(e, 'timer_from')}
                                   data-testid={`gg-prize-timer-from-${id}`}
                                   className={`${field} w-28`} />
                            {row.timer_from
                                ? (
                                    <button type="button" disabled={busy}
                                            onClick={() => patch({ timer_from: '' })}
                                            data-testid={`gg-prize-timer-from-clear-${id}`}
                                            className={clearBtn}>
                                        {t('goalGame.prizeTimerClear')}
                                    </button>
                                )
                                : (
                                    <span className="text-[11px] text-slate-400"
                                          data-testid={`gg-prize-timer-from-blank-${id}`}>
                                        {t('goalGame.prizeTimerFromBlank')}
                                    </span>
                                )}
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                            {t('goalGame.prizeTimerTo')}
                            <input type="time" value={row.timer_to || ''}
                                   onChange={(e) => patch({ timer_to: e.target.value })}
                                   onBlur={(e) => clearedOnBlur(e, 'timer_to')}
                                   data-testid={`gg-prize-timer-to-${id}`}
                                   className={`${field} w-28`} />
                            {row.timer_to
                                ? (
                                    <button type="button" disabled={busy}
                                            onClick={() => patch({ timer_to: '' })}
                                            data-testid={`gg-prize-timer-to-clear-${id}`}
                                            className={clearBtn}>
                                        {t('goalGame.prizeTimerClear')}
                                    </button>
                                )
                                : (
                                    <span className="text-[11px] text-slate-400"
                                          data-testid={`gg-prize-timer-to-blank-${id}`}>
                                        {t('goalGame.prizeTimerToBlank')}
                                    </span>
                                )}
                        </label>
                        <TimerChip row={row} testid={`gg-prize-timer-chip-${id}`} />
                    </>
                )}
            </div>
            {/* THE TIMER IN PLAIN WORDS, on or off. */}
            <p className="text-[11px] text-slate-500" data-testid={`gg-prize-timer-words-${id}`}>
                {row.timer_enabled
                    ? t('goalGame.prizeTimerOn', {
                        from: row.timer_from
                            ? prettyTime(row.timer_from)
                            : t('goalGame.prizeTimerNightStart'),
                        to: row.timer_to
                            ? prettyTime(row.timer_to)
                            : t('goalGame.prizeTimerNightEnd'),
                    })
                    : t('goalGame.prizeTimerOff')}
            </p>
            {row.timer_enabled && (
                <p className="text-[11px] text-slate-400" data-testid={`gg-prize-timer-hint-${id}`}>
                    {t('goalGame.prizeTimerHint')}
                </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
                {/* WHEN IT CAN BE USED. Where the answer is forced — a gift
                    card for the next game, or a prize set to At home — there is
                    nothing to choose, so the row SAYS so in normal words with a
                    small lock instead of showing a greyed-out control that
                    looks broken. Next to it, how long it stays valid. */}
                {useLocked ? (
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300"
                              data-testid={`gg-prize-use-locked-${id}`}>
                            <Lock size={13} className="shrink-0" aria-hidden="true" />
                            {t(row.kind === 'gift_card_next_game'
                                ? 'goalGame.prizeUseLockedGiftCard'
                                : 'goalGame.prizeUseLockedHome')}
                        </span>
                        <span className="inline-flex items-center gap-1.5"
                              data-testid={`gg-prize-valid-days-${id}`}>
                            <span className="text-slate-600 dark:text-slate-300">
                                {t('goalGame.prizeValidDays', { days })}
                            </span>
                            <button type="button" disabled={daysBusy || days <= 1}
                                    onClick={() => saveDays(days - 1)}
                                    title={t('goalGame.prizeValidLess')}
                                    aria-label={t('goalGame.prizeValidLess')}
                                    data-testid={`gg-prize-valid-less-${id}`}
                                    className={clearBtn}>−</button>
                            <button type="button" disabled={daysBusy}
                                    onClick={() => saveDays(days + 1)}
                                    title={t('goalGame.prizeValidMore')}
                                    aria-label={t('goalGame.prizeValidMore')}
                                    data-testid={`gg-prize-valid-more-${id}`}
                                    className={clearBtn}>+</button>
                            {days !== baseDays && (
                                <button type="button" disabled={daysBusy}
                                        onClick={() => saveDays(baseDays)}
                                        data-testid={`gg-prize-valid-putback-${id}`}
                                        className={clearBtn}>
                                    {t('goalGame.prizeValidPutBack')}
                                </button>
                            )}
                        </span>
                    </div>
                ) : (
                    <label className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2 text-xs text-slate-500">
                        {t('goalGame.prizeUse')}
                        <select className={`${field} w-56`} value={row.use_when || 'tonight'}
                                disabled={busy}
                                onChange={(e) => patch({ use_when: e.target.value })}
                                data-testid={`gg-prize-use-${id}`}>
                            <option value="tonight">{t('goalGame.prizeUseTonight')}</option>
                            <option value="next_visit">{t('goalGame.prizeUseNextVisit')}</option>
                        </select>
                    </label>
                )}
                <label className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2 text-xs text-slate-500">
                    {t('goalGame.prizeStarDouble')}
                    <select className={`${field} w-48`} value={row.star_double || 'none'}
                            disabled={busy}
                            onChange={(e) => patch({ star_double: e.target.value })}
                            data-testid={`gg-prize-star-double-${id}`}>
                        <option value="none">{t('goalGame.prizeStarDoubleNone')}</option>
                        <option value="value">{t('goalGame.prizeStarDoubleValue')}</option>
                    </select>
                </label>
                {/* WHO CAN WIN IT. Three pills on the prize row itself — one
                    tap either way, nothing to open, never a one-way door. A
                    table's scratch only ever draws On location or Both; a
                    player at home only ever gets At home or Both. */}
                <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2 text-xs text-slate-500">
                    {t('goalGame.prizeWho')}
                    <div role="group" aria-label={t('goalGame.prizeWho')}
                         data-testid={`gg-prize-who-${id}`}
                         className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 p-0.5">
                        {['bar', 'home', 'both'].map((key) => {
                            const on = whoOf(row) === key;
                            return (
                                <button key={key} type="button" disabled={busy}
                                        aria-pressed={on}
                                        onClick={() => {
                                            if (on) return;
                                            // Back to On location also puts an item
                                            // prize back on "use tonight", so the
                                            // change undoes in the SAME one tap.
                                            patch(key === 'bar' && row.kind !== 'gift_card_next_game'
                                                ? { who_wins: key, use_when: 'tonight' }
                                                : { who_wins: key });
                                        }}
                                        data-testid={`gg-prize-who-${key}-${id}`}
                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                            on ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                                               : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                                    {t(`goalGame.prizeWho_${key}`)}
                                </button>
                            );
                        })}
                    </div>
                    <WhoChip row={row} testid={`gg-prize-who-chip-${id}`} />
                </div>
            </div>
            {/* IN PLAIN WORDS: what this prize is — something to use right
                there tonight, or a gift card for a future visit. */}
            <p className="text-[11px] text-slate-500" data-testid={`gg-prize-who-words-${id}`}>
                {t(`goalGame.prizeWhoWords_${whoOf(row)}`, { days: days || 30 })}
            </p>
            {useLocked && (
                <p className="text-[11px] text-slate-400" data-testid={`gg-prize-valid-where-${id}`}>
                    {t('goalGame.prizeValidWhere')}
                </p>
            )}
            {whoOf(row) === 'home' && Number(row.weight || 0) > 0 && (
                <p className="text-[11px] text-slate-400" data-testid={`gg-prize-who-hint-${id}`}>
                    {t('goalGame.prizeWhoHomeWeightHint')}
                </p>
            )}
            {showWeight && (
                <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-slate-500">
                        {t('goalGame.prizeWeight')}
                        <input type="number" min="0" value={row.weight ?? 1} disabled={busy}
                               onChange={(e) => setRow((r) => ({ ...r, weight: e.target.value }))}
                               onBlur={(e) => {
                                   const next = Number(e.target.value);
                                   if (next !== Number(prize.weight ?? 1)) patch({ weight: next });
                               }}
                               data-testid={`gg-prize-weight-field-${id}`}
                               className={`${field} w-20`} />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-500">
                        <input type="checkbox" checked={!!row.active} disabled={busy}
                               onChange={(e) => patch({ active: e.target.checked })}
                               data-testid={`gg-prize-active-field-${id}`} />
                        {t('goalGame.prizeActive')}
                    </label>
                </div>
            )}
        </div>
    );
};

export default PrizeGoalSettings;
